import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, useReducedMotion } from 'motion/react';
import { useUiStore } from '@/store/useUiStore';
import Reveal from '@/components/Reveal';
import './Ch2NewEra.css';

interface Card {
  num: string;
  title: string;
  body: string;
}
interface ValueItem {
  title: string;
  statement: string;
}

/* ---------- 2c · interactive Values ring ---------- */
function ValuesRing() {
  const { t } = useTranslation();
  const reduce = useReducedMotion();
  const items = t('newEra.values.items', { returnObjects: true }) as ValueItem[];
  const [active, setActive] = useState(0);
  const n = items.length;

  return (
    <div className="values-wrap">
      <div className="values-ring">
        {/* central hub shows the active value */}
        <div className="values-hub" aria-live="polite">
          <span className="values-hub-num">{String(active + 1).padStart(2, '0')}</span>
          <h4 className="values-hub-title">{items[active].title}</h4>
          <p className="values-hub-text">{items[active].statement}</p>
        </div>

        <div className="values-nodes" role="tablist" aria-label={t('newEra.values.title')}>
          {items.map((it, i) => {
            const angle = (i / n) * Math.PI * 2 - Math.PI / 2;
            const x = 50 + Math.cos(angle) * 43;
            const y = 50 + Math.sin(angle) * 43;
            return (
              <button
                key={i}
                role="tab"
                aria-selected={active === i}
                className="values-node"
                data-active={active === i}
                style={{ ['--x' as string]: `${x}%`, ['--y' as string]: `${y}%` }}
                onMouseEnter={() => setActive(i)}
                onFocus={() => setActive(i)}
                onClick={() => setActive(i)}
              >
                <span className="values-node-num">{String(i + 1).padStart(2, '0')}</span>
                <span className="values-node-title">{it.title}</span>
              </button>
            );
          })}
          {!reduce && (
            <motion.span
              className="values-orbit"
              aria-hidden="true"
              animate={{ rotate: 360 }}
              transition={{ duration: 42, ease: 'linear', repeat: Infinity }}
            />
          )}
        </div>
      </div>
      <p className="values-hint">{t('newEra.values.hint')}</p>
    </div>
  );
}

export default function Ch2NewEra() {
  const { t } = useTranslation();
  const setActiveChapter = useUiStore((s) => s.setActiveChapter);
  const cards = t('newEra.buildFuture.cards', { returnObjects: true }) as Card[];
  const pillars = t('newEra.whoWeAre.pillars', { returnObjects: true }) as string[];
  const left = t('newEra.challenge.left', { returnObjects: true }) as string[];
  const right = t('newEra.challenge.right', { returnObjects: true }) as string[];

  return (
    <section
      id="new-era"
      className="chapter chapter--tint chapter-newera"
      aria-labelledby="newera-title"
      onMouseEnter={() => setActiveChapter('new-era')}
    >
      <div className="chapter-inner">
        {/* 2a — Kingdom builds the future */}
        <div className="chapter-head chapter-head--center">
          <p className="section-kicker">{t('newEra.kicker')}</p>
          <h2 id="newera-title" className="chapter-title">
            {t('newEra.buildFuture.title')}
          </h2>
          <p className="chapter-lead">{t('newEra.buildFuture.intro')}</p>
        </div>

        {/* Vision 2030 three pillars */}
        <div className="vision-axes">
          <span className="vision-axes-label">{t('newEra.buildFuture.visionAxesLabel')}</span>
          <div className="vision-axes-row">
            {(t('newEra.buildFuture.visionAxes', { returnObjects: true }) as string[]).map((ax) => (
              <span key={ax} className="vision-axis">
                {ax}
              </span>
            ))}
          </div>
        </div>

        <div className="grid-3 newera-cards">
          {cards.map((c, i) => (
            <Reveal key={c.num} delay={i * 0.08}>
              <article className="card newera-card">
                <span className="card-num">{c.num}</span>
                <h3 className="card-title">{c.title}</h3>
                <p className="card-body">{c.body}</p>
              </article>
            </Reveal>
          ))}
        </div>

        {/* 2b — Who we are / Vision / Mission */}
        <div className="newera-who">
          <Reveal className="who-intro">
            <h3 className="who-heading">{t('newEra.whoWeAre.title')}</h3>
            <p className="who-lead">{t('newEra.whoWeAre.intro')}</p>
            <div className="who-pillars">
              {pillars.map((p) => (
                <span key={p} className="chip chip--accent">
                  {p}
                </span>
              ))}
            </div>
            <p className="who-founded">
              <span className="who-founded-year">2025</span>
              <span className="who-founded-label">{t('newEra.whoWeAre.aligned')}</span>
            </p>
          </Reveal>
          <div className="who-vm">
            <Reveal className="glass vm-block" delay={0.08}>
              <span className="vm-label">{t('newEra.whoWeAre.visionLabel')}</span>
              <p className="vm-text">{t('newEra.whoWeAre.vision')}</p>
            </Reveal>
            <Reveal className="glass vm-block" delay={0.16}>
              <span className="vm-label">{t('newEra.whoWeAre.missionLabel')}</span>
              <p className="vm-text">{t('newEra.whoWeAre.mission')}</p>
            </Reveal>
          </div>
        </div>

        {/* 2c — Values ring */}
        <div className="newera-values">
          <div className="chapter-head chapter-head--center">
            <h3 className="chapter-title">{t('newEra.values.title')}</h3>
          </div>
          <ValuesRing />
        </div>

        {/* 2d — The challenge */}
        <div className="newera-challenge">
          <Reveal>
            <h3 className="challenge-title">{t('newEra.challenge.title')}</h3>
          </Reveal>
          <div className="challenge-split">
            <Reveal className="glass challenge-col challenge-col--soft" delay={0.06}>
              <h4 className="challenge-col-title">{t('newEra.challenge.leftTitle')}</h4>
              <ul className="challenge-list challenge-list--muted">
                {left.map((x) => (
                  <li key={x}>{x}</li>
                ))}
              </ul>
            </Reveal>
            <Reveal className="glass challenge-col challenge-col--hot" delay={0.14}>
              <h4 className="challenge-col-title">{t('newEra.challenge.rightTitle')}</h4>
              <ul className="challenge-list">
                {right.map((x) => (
                  <li key={x}>{x}</li>
                ))}
              </ul>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <p className="challenge-kicker">{t('newEra.challenge.kicker')}</p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
